import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import service from "../../services/api/service";
import { decrypt, decryptObject, encrypt } from "../../utils/crypto";
import { userSelector } from "../../services/redux/slices/authSlice";
import { useAuth } from "../../hooks/useAuth";

// qrc/user-verification's "data" field comes back as an encrypted blob when payload
// encryption is on (and as a plain array when it's off) — decryptObject handles both,
// same pattern as qrc/otp/verify's "data" field.
export const parseUserVerificationRecords = (data: any): any[] => {
  const parsed = typeof data === "string" ? decryptObject(data) : data;
  return Array.isArray(parsed) ? parsed : [];
};

// user-verification uses the literal string "NA" for a field that hasn't been issued yet, and
// "NA" is truthy — so it has to be filtered explicitly or it lands in the LAN dropdown as a
// selectable option.
export const cleanIdentifier = (value: any): string => {
  const text = value == null ? "" : String(value).trim();
  return text && text.toUpperCase() !== "NA" ? text : "";
};

// A matched record carries two buckets: disbursed loans (`loan`) and in-flight applications
// (`application`).
//
// One identifier per entry: the LAN once it has been issued, otherwise the CRM id standing in
// for it. Either one means the visitor is an existing customer. The first entry pre-fills the
// Raise Request "Loan Account/Application Number" input, all of them feed its dropdown.
export const extractLanList = (records: any[]): string[] => {
  const identifiers = records.flatMap((record: any) =>
    [
      ...(Array.isArray(record?.loan) ? record.loan : []),
      ...(Array.isArray(record?.application) ? record.application : []),
    ].map((entry: any) => cleanIdentifier(entry?.lan) || cleanIdentifier(entry?.crmId))
  );
  // One mobile can match several customer records (different CIFs sharing a contact number), so
  // the same identifier can arrive from both buckets and from more than one record — each repeat
  // would otherwise become a duplicate row in the Raise Request / View Tickets dropdowns. Keyed
  // on the upper-cased value so a casing difference between two records still counts as one LAN,
  // while the first spelling seen is what gets displayed.
  const seen = new Set<string>();
  return identifiers.filter((id) => {
    if (!id) return false;
    const key = id.toUpperCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

// The application bucket's appId, which the ticket-listing payload sends as applicationId.
// Kept apart from extractLanList: that one feeds the dropdowns and must not change.
export const extractAppIds = (records: any[]): string[] => {
  const ids = records.flatMap((record: any) =>
    (Array.isArray(record?.application) ? record.application : []).map((entry: any) =>
      cleanIdentifier(entry?.appId)
    )
  );
  const seen = new Set<string>();
  return ids.filter((id) => {
    if (!id) return false;
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
};

// One in-flight request per identifier, shared by every caller on the page: Raise a Request and
// View Tickets both want this list, and the Contact Us hub can mount them in turn.
interface QrcLookup {
  identifiers: string[];
  appIds: string[];
}

const EMPTY_LOOKUP: QrcLookup = { identifiers: [], appIds: [] };

const identifierRequests: Record<string, Promise<QrcLookup>> = {};

export const fetchQrcIdentifiers = (identifier: string): Promise<QrcLookup> => {
  const key = identifier.trim();
  if (!key) return Promise.resolve(EMPTY_LOOKUP);
  if (!identifierRequests[key]) {
    // Same service wrapper the rest of the app uses, so the base URL, headers and token all
    // come from the shared interceptor.
    identifierRequests[key] = service.support
      .userVerification({ identifier: encrypt(key) })
      .then((res) => {
        if (res?.data?.status?.toLowerCase() !== "success") return EMPTY_LOOKUP;
        const records = parseUserVerificationRecords(res?.data?.data);
        return { identifiers: extractLanList(records), appIds: extractAppIds(records) };
      })
      .catch(() => {
        // Not cached on failure, so the next screen retries rather than inheriting an empty list.
        delete identifierRequests[key];
        return EMPTY_LOOKUP;
      });
  }
  return identifierRequests[key];
};

// userProfile stores contact details encrypted; decrypt returns "" for anything that isn't
// valid ciphertext, and returns its input unchanged when payload encryption is off — so both
// shapes resolve here.
const readProfileValue = (value: any): string => {
  if (!value) return "";
  const decrypted = decrypt(String(value));
  return (decrypted || String(value)).trim();
};

/**
 * The LAN / CRM id list for the *logged-in* customer, from qrc/user-verification — the same
 * source and shape the pre-login QRC journey feeds its dropdowns from (see HelpCenter).
 * Post-login the portal otherwise only knows disbursed loan accounts, so an application still
 * awaiting a LAN never appeared in the Raise a Request / View Tickets dropdowns.
 *
 * Returns [] for a pre-login visitor: their list already lives in auth.qrcLanOptions.
 */
const useQrcLookup = (): QrcLookup & { settled: boolean } => {
  const { isAuthenticated } = useAuth();
  const userProfile = useSelector(userSelector);
  const [lookup, setLookup] = useState<QrcLookup>(EMPTY_LOOKUP);
  // Tells an empty list apart from one that simply hasn't arrived — both read as [] otherwise,
  // and a caller waiting on "the identifiers are final" cannot act on the difference.
  const [settled, setSettled] = useState(false);

  // Verification is keyed on the registered mobile, with the registered email as the fallback —
  // the same two identifiers the pre-login journey accepts.
  const identifier =
    readProfileValue(userProfile?.phoneNumber) || readProfileValue(userProfile?.email);

  useEffect(() => {
    if (!isAuthenticated || !identifier) {
      setLookup(EMPTY_LOOKUP);
      // Nothing is going to be looked up, so this is as final as it gets.
      setSettled(true);
      return;
    }
    let active = true;
    setSettled(false);
    fetchQrcIdentifiers(identifier).then((result) => {
      if (active) {
        setLookup(result);
        setSettled(true);
      }
    });
    return () => {
      active = false;
    };
  }, [isAuthenticated, identifier]);

  // Spread, not a new lookup object: `identifiers` and `appIds` keep their state identity, so
  // callers keying effects on them don't re-fire every render.
  return { ...lookup, settled };
};

// What the dropdowns list — unchanged.
export const useQrcIdentifiers = (): string[] => useQrcLookup().identifiers;

// The appId values for the logged-in customer, for the ticket-listing payload.
export const useQrcAppIds = (): string[] => useQrcLookup().appIds;

// Whether the look-up above has finished. An empty identifier list means "this customer has
// none", not "not yet" — which is what View Tickets needs before it clamps a restored page
// against a total built from a possibly incomplete LAN filter.
export const useQrcIdentifiersSettled = (): boolean => useQrcLookup().settled;